/**
 * DSAccount Auth Service
 * Handles SSO authentication with DSAccount
 */

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: string;
  tenant_id?: string;
}

export interface AuthState {
  user: User | null;
  loading: boolean;
  error: string | null;
}

const API_BASE = import.meta.env.VITE_API_BASE || '/api';
const DSACCOUNT_URL = import.meta.env.VITE_DSACCOUNT_URL || 'https://account.digistratum.com';

/**
 * Generate a cryptographically secure random state parameter
 */
function generateState(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

class AuthService {
  /**
   * Check current auth status - tries session cookie first
   */
  async me(): Promise<User> {
    const response = await fetch(`${API_BASE}/auth/me`, {
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Not authenticated');
    }

    return response.json();
  }

  /**
   * Initiate SSO login flow
   * Stores state and redirect URL, then redirects to backend SSO endpoint
   */
  initiateSSO(returnUrl?: string): void {
    // Generate and store state for CSRF protection
    const state = generateState();
    sessionStorage.setItem('sso_state', state);
    
    // Store current URL for redirect after auth
    const currentPath = returnUrl || window.location.pathname + window.location.search;
    sessionStorage.setItem('sso_redirect', currentPath);
    
    // Redirect to backend SSO initiation endpoint
    // The backend will redirect to DSAccount with proper app credentials
    const ssoUrl = `${API_BASE}/auth/sso?return_url=${encodeURIComponent(window.location.origin + currentPath)}`;
    window.location.href = ssoUrl;
  }

  /**
   * Logout - clears session and optionally redirects to DSAccount logout
   */
  async logout(redirectToDSAccount = false): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/auth/logout`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok && redirectToDSAccount) {
        const data = await response.json();
        if (data.logout_url) {
          window.location.href = data.logout_url;
          return;
        }
      }
    } catch (err) {
      console.error('Logout error:', err);
    }
    
    // Reload to clear any cached state
    window.location.href = '/';
  }

  /**
   * Refresh session
   */
  async refreshSession(): Promise<{ expiresAt: string }> {
    const response = await fetch(`${API_BASE}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
    });

    if (!response.ok) {
      throw new Error('Failed to refresh session');
    }

    return response.json();
  }

  /**
   * Get DSAccount URL for account management
   */
  getAccountUrl(): string {
    return DSACCOUNT_URL;
  }
}

export const authService = new AuthService();
export default authService;
